/**
 * Signal-chart slice — the shared, READ-ONLY view of the `bot_all` account
 * (the operator's production bot), broadcast to every signed-in user as a
 * reference. Mirrors `tradingSlice`, but:
 *   - it is a DIFFERENT account from the user's own (`trading` slice), so it
 *     lives in its own slice and never mixes with the user's positions;
 *   - it is sourced from `/api/signal/*` (public, read-only), not
 *     `/api/trading/*` (owner/trader, execution-capable);
 *   - `enabled` / `online` gate the UI: the rail toggle only shows when the
 *     feature is enabled server-side, and the pane shows "source offline" when
 *     `online` is false (bot_all not running).
 *
 * Live updates arrive via the `/api/signal/stream` SSE (a `status` event then
 * the worker's `snapshot`/`account`/`positions`/`orders` events). History is a
 * one-shot REST fetch for the closed-trade markers.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { api } from "@/api/client";
import type {
  TradingAccountInfo,
  TradingAccountResponse,
  TradingDeal,
  TradingHistoryResponse,
  TradingOrder,
  TradingOrdersResponse,
  TradingPosition,
  TradingPositionsResponse,
  SignalStatusResponse,
} from "@/api/types";

/** Reserved tab id for the signal chart's Chart instance. NOT a real tab (never
 *  in `tabs.tabs`, the TabBar, or a workspace) — its per-tab ui/ohlc state falls
 *  back to defaults (XAUUSD/1H), which is exactly what bot_all trades. Rendering
 *  a `<Chart tabId={SIGNAL_TAB_ID} signalMode/>` reuses the whole chart stack
 *  read-only without polluting the tab system. */
export const SIGNAL_TAB_ID = "__signal__";

interface SignalState {
  /** Feature switch (server config). The rail toggle is hidden when false. */
  enabled: boolean;
  /** bot_all terminal is live + connected (distinct from `enabled`). */
  online: boolean;
  /** Symbol the pane locks to (bot_all is single-symbol; default XAUUSD). */
  symbol: string;
  /** Whether the source's last poll actually reached the account. */
  connected: boolean;
  account: TradingAccountInfo | null;
  positions: TradingPosition[];
  orders: TradingOrder[];
  history: { deals: TradingDeal[]; orders: TradingOrder[] };
  streamConnected: boolean;
  historyStatus: "idle" | "loading" | "succeeded" | "failed";
  lastPolledAt: number | null;
  error: string | null;
}

const initialState: SignalState = {
  enabled: false,
  online: false,
  symbol: "XAUUSD",
  connected: false,
  account: null,
  positions: [],
  orders: [],
  history: { deals: [], orders: [] },
  streamConnected: false,
  historyStatus: "idle",
  lastPolledAt: null,
  error: null,
};

interface SignalStatusEvent {
  online: boolean;
  symbol: string;
}

interface SignalStreamSnapshot {
  warming?: boolean;
  account?: TradingAccountResponse;
  positions?: TradingPositionsResponse;
  orders?: TradingOrdersResponse;
}

/** One-shot status probe — drives whether the rail toggle renders and which
 *  symbol the pane locks to, before/independent of the SSE. */
export const fetchSignalStatus = createAsyncThunk<SignalStatusResponse>(
  "signal/status",
  async () => api.signalStatus(),
);

export const fetchSignalHistory = createAsyncThunk<
  TradingHistoryResponse,
  { days?: number } | undefined
>("signal/history", async (arg) => api.signalHistory(arg?.days));

const signalSlice = createSlice({
  name: "signal",
  initialState,
  reducers: {
    /** The SSE `status` event — the source's live/offline state + symbol. */
    applySignalStatus: (state, action: PayloadAction<SignalStatusEvent>) => {
      state.online = action.payload.online;
      if (action.payload.symbol) state.symbol = action.payload.symbol;
    },
    applySignalSnapshot: (state, action: PayloadAction<SignalStreamSnapshot>) => {
      const { account, positions, orders } = action.payload;
      if (account) {
        state.connected = account.connected;
        state.account = account.account;
      }
      if (positions) {
        state.connected = positions.connected;
        state.positions = positions.positions;
      }
      if (orders) {
        state.orders = orders.orders;
      }
      if (account || positions || orders) {
        state.lastPolledAt = Date.now();
      }
    },
    applySignalAccount: (state, action: PayloadAction<TradingAccountResponse>) => {
      state.connected = action.payload.connected;
      state.account = action.payload.account;
      state.lastPolledAt = Date.now();
    },
    applySignalPositions: (state, action: PayloadAction<TradingPositionsResponse>) => {
      state.connected = action.payload.connected;
      state.positions = action.payload.positions;
      state.lastPolledAt = Date.now();
    },
    applySignalOrders: (state, action: PayloadAction<TradingOrdersResponse>) => {
      state.orders = action.payload.orders;
      state.lastPolledAt = Date.now();
    },
    setSignalStreamConnected: (state, action: PayloadAction<boolean>) => {
      state.streamConnected = action.payload;
      // A dropped stream doesn't prove the source is offline (could be a
      // network blip); leave `online` for the next status event to set.
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSignalStatus.fulfilled, (state, action) => {
        state.enabled = action.payload.enabled;
        state.online = action.payload.online;
        if (action.payload.symbol) state.symbol = action.payload.symbol;
      })
      .addCase(fetchSignalStatus.rejected, (state) => {
        state.enabled = false;
      })
      .addCase(fetchSignalHistory.pending, (state) => {
        state.historyStatus = "loading";
      })
      .addCase(fetchSignalHistory.fulfilled, (state, action) => {
        state.historyStatus = "succeeded";
        state.history = {
          deals: action.payload.deals,
          orders: action.payload.orders,
        };
      })
      .addCase(fetchSignalHistory.rejected, (state, action) => {
        state.historyStatus = "failed";
        state.error = action.error.message ?? "Failed to fetch signal history";
      });
  },
});

export const {
  applySignalStatus,
  applySignalSnapshot,
  applySignalAccount,
  applySignalPositions,
  applySignalOrders,
  setSignalStreamConnected,
} = signalSlice.actions;

export default signalSlice.reducer;
